import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { pagesApi, homeApi } from "../services/api";
import { config } from "../config/config";
import mediaService from "../services/mediaService";
import HeroSection from "../components/HeroSection";
import { RichContent } from "../components/RichContent";

function HomePage() {
  const [homePage, setHomePage] = useState(null);
  const [heroData, setHeroData] = useState(null);
  const [aboutSection, setAboutSection] = useState(null);
  const [sectionHeadings, setSectionHeadings] = useState(null);
  const [featuredPrograms, setFeaturedPrograms] = useState([]);
  const [services, setServices] = useState([]);
  const [latestInsights, setLatestInsights] = useState([]);
  const [email, setEmail] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    loadHomeData();
  }, []);

  // Auto-rotate slider
  useEffect(() => {
    if (heroData?.use_slider && heroData?.slider_images?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroData.slider_images.length);
      }, heroData.slider_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [heroData]);

  const loadHomeData = async () => {
    try {
      // Load home page content
      const pageResponse = await pagesApi.getBySlug("home");
      setHomePage(pageResponse.data);

      // Load hero section with background image
      const heroResponse = await fetch(`${config.api.baseURL}/homepage/hero`);
      if (heroResponse.ok) {
        const heroContent = await heroResponse.json();
        setHeroData(heroContent);
      }

      // Load featured programs
      const programsResponse = await homeApi.getFeaturedPrograms();
      setFeaturedPrograms(programsResponse.data);

      // Load services
      const servicesResponse = await homeApi.getServices();
      setServices(servicesResponse.data);

      // Load latest insights
      const insightsResponse = await homeApi.getLatestInsights();
      setLatestInsights(insightsResponse.data);

      // Load about section
      const aboutResponse = await fetch(`${config.api.baseURL}/homepage/about`);
      console.log("About Response:", aboutResponse);
      if (aboutResponse.ok) {
        const aboutContent = await aboutResponse.json();
        setAboutSection(aboutContent);
      }

      // Load homepage section headings
      const sectionsResponse = await fetch(
        `${config.api.baseURL}/homepage/sections`,
      );
      if (sectionsResponse.ok) {
        const sectionsContent = await sectionsResponse.json();
        setSectionHeadings(sectionsContent);
      }
    } catch (error) {
      console.error("Error loading home data:", error);
    }
  };

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    try {
      await homeApi.subscribeNewsletter({ email });
      alert("Subscribed successfully!");
      setEmail("");
    } catch (error) {
      alert("Subscription failed. Please try again.");
    }
  };

  return (
    <div>
      <HeroSection
        title={
          heroData?.headline ||
          homePage?.hero_headline ||
          "Become a better leader, transform your business"
        }
        subtitle={
          heroData?.subheadline ||
          homePage?.hero_subheadline ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={heroData?.background_image}
        useSlider={heroData?.use_slider}
        sliderImages={heroData?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={heroData?.cta_text || homePage?.cta_text}
      >
        {!heroData?.cta_text && !homePage?.cta_text && (
          <Link
            to="/contact"
            className="bg-white text-blue-600 px-8 py-3 rounded-full text-lg font-semibold hover:bg-gray-100 transition inline-block"
          >
            Contact us for details
          </Link>
        )}
      </HeroSection>

      {/* About Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                {aboutSection?.title || "About Innobig"}
              </h2>
              <p className="text-gray-600 mb-6">
                <RichContent
                  html={
                    aboutSection?.content ||
                    "A professional development hub founded by Mohammad Abdul Matin Emon, dedicated to empowering growth through innovation and leadership."
                  }
                />
              </p>
              <Link
                to="/about"
                className="text-blue-600 font-semibold hover:underline"
              >
                Learn more about our story →
              </Link>
            </div>
            <div className="relative">
              {aboutSection?.founder_image ? (
                <img
                  src={
                    aboutSection.founder_image.startsWith("http")
                      ? aboutSection.founder_image
                      : mediaService.getFileUrl(aboutSection.founder_image)
                  }
                  alt="Founder"
                  className="h-64 w-full rounded-lg object-cover"
                />
              ) : (
                <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center">
                  <span className="text-gray-500">Founder Photo</span>
                </div>
              )}
              <div className="mt-4 p-6 bg-blue-50 rounded-lg">
                <h3 className="font-bold mb-2">Mohammad Abdul Matin Emon</h3>
                <p className="text-gray-600 text-sm">
                  <RichContent
                    html={
                      aboutSection?.founder_story ||
                      "Former Doctorola & Praava Health executive turned entrepreneur, sharing lessons from failures and successes."
                    }
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">
              {sectionHeadings?.programs_title || "Flagship Programs"}
            </h2>
            {sectionHeadings?.programs_subtitle && (
              <p className="text-gray-600 mt-3 max-w-3xl mx-auto">
                {sectionHeadings.programs_subtitle}
              </p>
            )}
            <Link
              to="/programs"
              className="text-blue-600 font-semibold hover:underline"
            >
              Explore All Engagements →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredPrograms.map((program) => (
              <div
                key={program.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition"
              >
                <div className="p-6">
                  <div className="text-4xl mb-4">{program.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{program.title}</h3>
                  <p className="text-gray-600 mb-4">{program.description}</p>
                  {program.cta_text && (
                    <Link
                      to={`/programs/${program.slug}`}
                      className="text-blue-600 font-semibold hover:underline"
                    >
                      {program.cta_text}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">
              {sectionHeadings?.services_title || "Our Services"}
            </h2>
            {sectionHeadings?.services_subtitle && (
              <p className="text-gray-600 mt-3 max-w-3xl mx-auto">
                {sectionHeadings.services_subtitle}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-8 text-center hover:border-blue-300 transition"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                {service.cta_text && (
                  <Link
                    to={service.cta_href || `/${service.slug}`}
                    className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition"
                  >
                    {service.cta_text}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Insights */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold">
              Practical Insights on Strategy, Growth & Execution
            </h2>
            <Link
              to="/insights"
              className="text-blue-600 font-semibold hover:underline"
            >
              View All Insights →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {latestInsights.map((post) => (
              <div
                key={post.id}
                className="bg-white rounded-lg overflow-hidden shadow hover:shadow-md transition"
              >
                {/* Featured Image url should be displayed here */}
                {post.featured_image && (
                  <div className="h-48 bg-gray-200 flex items-center justify-center">
                    {/* <span className="text-gray-500"></span> */}
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-bold text-lg mb-2">{post.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                  <Link
                    to={`/insights/${post.slug}`}
                    className="text-blue-600 text-sm font-semibold hover:underline"
                  >
                    Read More →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="mb-8">
            Join our mailing list for the latest insights, program updates, and
            exclusive content.
          </p>
          <form
            onSubmit={handleNewsletterSubscribe}
            className="max-w-md mx-auto"
          >
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-gray-800"
                required
              />
              <button
                type="submit"
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Subscribe
              </button>
            </div>
            <p className="text-sm text-blue-100 mt-2">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
